/**
 * Reading a PO's receiving state out of `asn.arrivals[]`.
 *
 * WHAT THE SHAPE MEANS. Pipe17 keeps receiving detail off the purchase record
 * entirely, in two entities the provider walks and nests for us:
 *
 *   arrival  — one PHYSICAL DELIVERY against the PO. Several per PO is what
 *              "partially received" actually means. Its line items say what the
 *              delivery was expected to contain and what it recorded as landed.
 *   receipt  — one GOODS-IN BOOKING against an arrival. This is the warehouse's
 *              own count, and it is the only place a real received-on date
 *              exists: neither the arrival nor the purchase carries one.
 *
 * ARRIVED AND RECEIVED ARE NOT THE SAME NUMBER, which is the entire reason this
 * file computes them separately rather than picking whichever is present:
 *   - arrived  = the goods showed up at the door (arrival line
 *                `quantity_received`, or its expected quantity once the arrival
 *                is in a received state).
 *   - received = the warehouse counted them in (summed receipt line
 *                `quantity`).
 * A delivery sitting on the dock is arrived and not received; a short or
 * damaged count is arrived-but-not-fully-received. Collapsing the two would
 * hide exactly the discrepancy an operator opens this view to find.
 *
 * `quantity_damaged` is a SUBSET of a receipt's `quantity`, not a separate
 * bucket to add on: the goods were booked in and are unusable. It is surfaced
 * on its own so a short receipt is distinguishable from a damaged one, and it is
 * never subtracted here.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** A quantity the provider actually reported, as opposed to one it omitted.
 *  The distinction is load-bearing: an unreported quantity must not render as
 *  a confident zero. */
const reported = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);

export const hasArrivals = (asn) =>
  Array.isArray(asn?.arrivals) && asn.arrivals.length > 0;

/** Every receipt on the PO, flattened out of its arrivals, each tagged with the
 *  arrival it was booked against so a flat list still says where it came from. */
export const allReceipts = (asn) =>
  (asn?.arrivals || []).flatMap((a) =>
    (a.receipts || []).map((r) => ({
      ...r,
      arrival_id: a.arrival_id,
      ext_arrival_id: a.ext_arrival_id,
    })),
  );

/**
 * How much of one arrival line physically turned up.
 *
 * Prefers the provider's reported `quantity_received`. Falls back to the
 * expected quantity ONLY when the arrival's own status says the whole delivery
 * landed — an arrival marked `received` with no per-line figure means the line
 * arrived in full, and reading that as 0 would report a delivered PO as never
 * having arrived.
 */
export const arrivedQty = (line, arrivalStatus) => {
  if (reported(line?.quantity_received)) return num(line.quantity_received);
  const s = String(arrivalStatus || "").toLowerCase();
  return s === "received" ? num(line?.quantity_expected) : 0;
};

/** Per-SKU totals across every arrival and receipt on the PO. */
export const receivingBySku = (asn) => {
  const bySku = new Map();
  const row = (sku) => {
    if (!bySku.has(sku)) {
      bySku.set(sku, {
        sku,
        ordered: 0,
        expected: 0,
        arrived: 0,
        received: 0,
        damaged: 0,
      });
    }
    return bySku.get(sku);
  };

  // Ordered comes from the PO itself, so a SKU that was ordered but has not
  // arrived at all still gets a row rather than vanishing from the summary.
  for (const l of asn?.sku_list || []) {
    if (l?.sku) row(l.sku).ordered += num(l.quantity_requested);
  }

  for (const a of asn?.arrivals || []) {
    for (const l of a.line_items || []) {
      if (!l?.sku) continue;
      const r = row(l.sku);
      r.expected += num(l.quantity_expected);
      r.arrived += arrivedQty(l, a.status);
    }
    for (const rec of a.receipts || []) {
      for (const l of rec.line_items || []) {
        if (!l?.sku) continue;
        const r = row(l.sku);
        r.received += num(l.quantity);
        r.damaged += num(l.quantity_damaged);
      }
    }
  }

  return [...bySku.values()];
};

/** PO-wide totals — the numbers the summary strip shows. */
export const receivingTotals = (asn) => {
  const rows = receivingBySku(asn);
  const sum = (k) => rows.reduce((n, r) => n + r[k], 0);
  const ordered = sum("ordered");
  const received = sum("received");
  return {
    skus: rows.length,
    ordered,
    expected: sum("expected"),
    arrived: sum("arrived"),
    received,
    damaged: sum("damaged"),
    outstanding: Math.max(ordered - received, 0),
    // Guarded against a PO with no ordered quantity, which would otherwise be
    // NaN and render as "NaN%".
    receivedPct: ordered > 0 ? Math.round((received / ordered) * 100) : null,
    arrivals: (asn?.arrivals || []).length,
    receipts: allReceipts(asn).length,
  };
};

/**
 * The most recent goods-in date on the PO.
 *
 * Receipts are the only records in the chain that carry a real received-on
 * timestamp, so this is the PO's true "received on" — as opposed to
 * `others.actual_arrival_date`, which the provider derives from the most recent
 * ARRIVAL and therefore moves when goods reach the door, not when they are
 * counted in.
 */
export const lastReceivedDate = (asn) => {
  const times = allReceipts(asn)
    .map((r) => r.actual_arrival_date)
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !isNaN(t));
  return times.length ? new Date(Math.max(...times)).toISOString() : null;
};
